/*
 * Theme contract
 * ---------------------------------------------------------------------------
 * The admin UI expects project.theme to provide the semantic colours below.
 * Do not add silent fallback aliases here: missing theme keys should be fixed
 * in the project JSON, not hidden by code. This helper warns once per missing
 * path so bad theme config is visible during development.
 */

const REQUIRED_THEME_PATHS = [
  "columnHeaderBackgroundColour",
  "columnHeaderTextColour",
  "fontFamily",
  "fontSize",
  "headerBackgroundColour",
  "headerTextColour",
  "headerWidgetColour",
  "labelOptions.Accepted.colour",
  "labelOptions.Rejected.colour",
  "labelOptions.Withdrawn.colour",
  "mainColour",
  "secondaryColour",
  "MainTextColour",
  "progressBarColour",
  "reviewColour",
  "submissionColour",
  "submissionsUnderReviewColour",
  "userColour",
  "aiColour",
  "aiHeadlineColour",
  "aiMatchColour",
  "aiSuggestionColour",
  "aiAmbiguousColour",
  "aiNoPickColour",
  "aiNeutralColour",
  "successColour",
  "warningColour",
  "errorColour",
  "selectTokens.checkbox.checkedColour",
  "selectTokens.borders.panel.activeWidth",
  "selectTokens.borders.panel.default",
  "selectTokens.borders.panel.focus",
  "selectTokens.borders.panel.hover",
  "selectTokens.borders.panel.width",
  "selectTokens.flags.height",
  "selectTokens.flags.radius",
  "selectTokens.flags.width",
  "selectTokens.menu.elevation",
  "selectTokens.menu.hoverShadow",
  "selectTokens.menu.itemPadY",
  "selectTokens.menu.minWidth",
  "selectTokens.menu.selectedShadow",
  "selectTokens.metrics.grid.padX",
  "selectTokens.metrics.grid.padY",
  "selectTokens.metrics.grid.radius",
  "selectTokens.metrics.panel.maxWidth",
  "selectTokens.metrics.panel.padX",
  "selectTokens.metrics.panel.padY",
  "selectTokens.metrics.panel.radius",
  "selectTokens.pill.chipFontSize",
  "selectTokens.pill.chipPadX",
  "selectTokens.pill.chipPadY",
  "selectTokens.pill.chipRadius",
  "selectTokens.pill.minChipWidth",
  "selectTokens.readonly.panelBackground",
  "selectTokens.tree.leafOnly",
  "selectTokens.tree.popoverMaxHeight",
  "selectTokens.tree.popoverMinWidth",
  "selectTokens.tree.colours.bg",
  "selectTokens.tree.colours.branchFg",
  "selectTokens.tree.colours.descFg",
  "selectTokens.tree.colours.fg",
  "selectTokens.tree.colours.focusedRing",
  "selectTokens.tree.colours.highlightBg",
  "selectTokens.tree.colours.highlightFg",
  "selectTokens.tree.colours.hoverBg",
  "selectTokens.tree.colours.hoverFg",
  "selectTokens.tree.colours.selectedBg",
  "selectTokens.tree.colours.selectedFg",
  "selectTokens.typography.emptyColour",
  "selectTokens.typography.fontFamily",
  "selectTokens.typography.gridFontSize",
  "selectTokens.typography.panelFontSize",
  "selectTokens.typography.placeholderColour",
  "selectTokens.typography.textColour",
];

const warnedThemePaths = new Set();

function getPathValue(source, path) {
  return path.split(".").reduce((acc, key) => acc?.[key], source);
}

function warnMissingThemePaths(theme) {
  if (!theme) {
    if (!warnedThemePaths.has("<theme>")) {
      warnedThemePaths.add("<theme>");
      console.warn("[theme] Missing project.theme object.");
    }
    return;
  }

  const missing = REQUIRED_THEME_PATHS.filter((path) => {
    const value = getPathValue(theme, path);
    return value === undefined || value === null || value === "";
  }).filter((path) => !warnedThemePaths.has(path));

  if (!missing.length) return;

  missing.forEach((path) => warnedThemePaths.add(path));
  console.warn("[theme] Missing required theme values:", missing);
}

export const deriveTheme = (ct) => {
  warnMissingThemePaths(ct);

  const brand = ct?.mainColour ?? "#1D4D80";
  const brandLight = ct?.columnHeaderBackgroundColour ?? "#0098DB";
  const review = ct?.reviewColour ?? "#B565D8";
  const widget = ct?.headerWidgetColour ?? "#5A245A";
  const gold = ct?.headerTextColour ?? "#B4A76C";
  const submission = ct?.submissionColour ?? "#4A9EFF";
  const accepted = ct?.labelOptions?.Accepted?.colour ?? "#52c41a";
  const rejected = ct?.labelOptions?.Rejected?.colour ?? "#f5222d";
  const success = ct?.successColour ?? accepted;
  const warning = ct?.warningColour ?? "#F59E0B";
  const error = ct?.errorColour ?? rejected;
  const user = ct?.userColour ?? submission;
  const ai = ct?.aiColour ?? review;
  const aiHeadline = ct?.aiHeadlineColour ?? brandLight;
  const aiMatch = ct?.aiMatchColour ?? accepted;
  const aiSuggestion = ct?.aiSuggestionColour ?? ai;
  const aiAmbiguous = ct?.aiAmbiguousColour ?? warning;
  const aiNoPick = ct?.aiNoPickColour ?? "#7A9CC0";
  const aiNeutral = ct?.aiNeutralColour ?? "#315A7C";
  const fontFamily = ct?.selectTokens?.typography?.fontFamily
    ? `'${ct.selectTokens.typography.fontFamily}', system-ui, sans-serif`
    : "system-ui, -apple-system, sans-serif";

  return {
    panelBg: "#04081A",
    surface1: "#080F24",
    surface2: "#0C1530",
    borderSubtle: "#10203C",
    borderMedium: "#182E50",
    textPrimary: "#E8EEF6",
    textSecondary: "#7A9CC0",
    textMuted: "#344F6A",
    fontFamily,
    brand,
    brandLight,
    review,
    widget,
    gold,
    submission,
    accepted,
    rejected,
    success,
    warning,
    error,
    user,
    ai,
    aiHeadline,
    aiMatch,
    aiSuggestion,
    aiAmbiguous,
    aiNoPick,
    aiNeutral,
    successColour: success,
    warningColour: warning,
    errorColour: error,
    userColour: user,
    aiColour: ai,
    aiHeadlineColour: aiHeadline,
    aiMatchColour: aiMatch,
    aiSuggestionColour: aiSuggestion,
    aiAmbiguousColour: aiAmbiguous,
    aiNoPickColour: aiNoPick,
    aiNeutralColour: aiNeutral,
    decision: {
      ai_route: { grad: [brand, brandLight], glow: `${brandLight}55` },
      full_review: { grad: [widget, review], glow: `${review}55` },
      specialist: { grad: [brand, gold], glow: `${gold}55` },
    },
    gaugeColor: (pct) => {
      if (pct >= 0.85) return brandLight;
      if (pct >= 0.7) return submission;
      if (pct >= 0.5) return warning;
      if (pct >= 0.3) return "#F97316";
      return rejected;
    },
    risk: {
      specialist: { color: gold, symbol: "SPC", label: "Specialist Review" },
      structure: { color: rejected, symbol: "STR", label: "Structure Score" },
      alignment: { color: warning, symbol: "ALN", label: "Title Alignment" },
      vagueness: { color: review, symbol: "VAG", label: "Vagueness Flags" },
      confidence: { color: brandLight, symbol: "CONF", label: "Confidence Level" },
      outcome: { color: accepted, symbol: "QUAL", label: "Quality Score" },
    },
    avail: {
      available: accepted,
      busy: warning,
      unavailable: rejected,
      pending: "#5A6880",
    },
  };
};
